import React, { useState, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import Base from "../core/Base";
import {
  Box, Typography, Paper, TextField, Button, Grid, Alert, CircularProgress,
  FormControl, InputLabel, Select, MenuItem, Switch, FormControlLabel,
  Chip, Stack, Autocomplete, Table, TableBody, TableCell, TableHead,
  TableRow, IconButton, Divider, Accordion, AccordionSummary,
  AccordionDetails, ToggleButtonGroup, ToggleButton,
} from "@mui/material";
import ExpandMoreIcon  from "@mui/icons-material/ExpandMore";
import AddIcon         from "@mui/icons-material/Add";
import DeleteIcon      from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LinkIcon        from "@mui/icons-material/Link";
import ArrowBackIcon   from "@mui/icons-material/ArrowBack";
import { isAutheticated } from "../auth/helper";
import {
  getFeedGroups, createFeed, updateFeed, getFeed,
  validateFeedUrl, getAllCompanies,
} from "./helper/adminapicall";

// ── Classification lookup data ────────────────────────────────────────────────
const ASSET_OPTS  = ["EQUITIES","FIXED_INCOME","COMMODITIES","CURRENCIES","CRYPTO","DERIVATIVES","REAL_ESTATE","ALTERNATIVES"];
const SECTOR_OPTS = ["TECHNOLOGY","FINANCIALS","HEALTHCARE","ENERGY","CONSUMER","INDUSTRIALS","MATERIALS","UTILITIES","TELECOM","REAL_ESTATE","MEDIA"];
const TOPIC_OPTS  = ["EARNINGS","MACRO","POLICY","MERGER","DIVIDENDS","TRADING","ANALYSIS","CORPORATE","IPO","REGULATORY","ESG","RATINGS"];
const CT_OPTS     = ["CRAWLER","NEWS","ANALYSIS","PRESS_RELEASE","BLOG","REGULATORY","EARNINGS_RELEASE","RESEARCH"];
const FMT_OPTS    = ["CRAWLER","ARTICLE","SUMMARY","FLASH","BRIEF","REPORT","TRANSCRIPT"];

// ISO 3166-1 alpha-2 region codes (most-used in financial/news context)
const REGION_OPTS = [
  "IN","CN","JP","KR","SG","AU","NZ","MY","TH","VN","ID","PH","HK","TW","PK","BD","LK","MM",
  "US","CA","MX",
  "GB","DE","FR","IT","ES","NL","SE","NO","DK","FI","CH","AT","BE","PL","PT","RU","UA","GR","TR",
  "AE","SA","QA","KW","BH","OM","IL","EG","IR","IQ","JO","LB",
  "BR","AR","CL","CO","PE","VE",
  "ZA","NG","KE","GH","MA","TN","ET","TZ","EG",
  "GL", // GLOBAL shorthand
];
const CONT_OPTS = ["ASIA_PACIFIC","EUROPE","NORTH_AMERICA","SOUTH_AMERICA","MIDDLE_EAST","AFRICA","GLOBAL"];

// Continent → ISO region codes mapping (for auto-fill)
const CONTINENT_ISO_MAP = {
  ASIA_PACIFIC:   ["IN","CN","JP","KR","SG","AU","NZ","MY","TH","VN","ID","PH","HK","TW","PK","BD","LK","MM","MN","KH","LA","BN","AF","GE","AM","AZ","KZ","UZ","TJ","TM","KG"],
  EUROPE:         ["GB","DE","FR","IT","ES","NL","SE","NO","DK","FI","CH","AT","BE","PL","PT","RU","UA","GR","TR","RO","CZ","HU","SK","BG","HR","RS","SI","EE","LV","LT","IS","IE","LU","MT","CY","AL","MK","ME","BA","MD","BY"],
  NORTH_AMERICA:  ["US","CA","MX","GT","BZ","HN","SV","NI","CR","PA","CU","JM","HT","DO","TT","BB"],
  SOUTH_AMERICA:  ["BR","AR","CL","CO","PE","VE","EC","BO","PY","UY","GY","SR"],
  MIDDLE_EAST:    ["AE","SA","QA","KW","BH","OM","IL","EG","IR","IQ","JO","LB","SY","YE","LY","PS","TR"],
  AFRICA:         ["ZA","NG","KE","GH","MA","TN","ET","TZ","DZ","SD","AO","MZ","ZM","ZW","BW","NA","RW","CM","SN","CI","ML","NE","MR","LR","SL","GN","GW","BF","BI","DJ","ER","SO","CD","CG","CF","GA","GQ","CV","ST","KM","MG","MW","MU","LS","SZ","RE","SC"],
  GLOBAL:         [],
};
// Reverse map: ISO code → continent
const REGION_CONTINENT_MAP = {};
Object.entries(CONTINENT_ISO_MAP).forEach(([cont, regions]) => {
  regions.forEach(r => { REGION_CONTINENT_MAP[r] = cont; });
});

const INITIAL = {
  url:"", title:"", group_id:"", is_active:true,
  strip_title_source:true, source_name:"", source_code:"",
  content_type:"CRAWLER", format:"CRAWLER",
  poll_interval_mode:"global", poll_interval_seconds:900, poll_random:false,
  asset_classes:[], sectors:[], topic_codes:[], tags:[],
  regions:[], continents:[],
  symbols:[], symbol_rules:[],
  breaking_keywords:["BREAKING","ALERT","URGENT","FLASH"],
  ignore_keywords:[], ignore_domains:[],
  region_rules:[],
};

// ── ChipInput: Autocomplete multiple freeSolo ─────────────────────────────────
// Renders chips inside the input — MUI handles deletion natively (fixes individual-chip clear bug).
const ChipInput = ({ label, value = [], onChange, options = [], placeholder = "", lowercase = false }) => (
  <Autocomplete
    multiple
    freeSolo
    options={options.filter(o => !value.includes(o))}
    value={value}
    onChange={(_, newValue) => {
      const cleaned = newValue.map(v => {
        const s = typeof v === "string" ? v : (v.inputValue || String(v));
        return lowercase ? s.trim().toLowerCase() : s.trim().toUpperCase().replace(/\s+/g, "_");
      }).filter(Boolean);
      onChange([...new Set(cleaned)]);
    }}
    renderTags={(tagValue, getTagProps) =>
      tagValue.map((option, index) => (
        <Chip {...getTagProps({ index })} key={`${option}-${index}`} label={option} size="small" />
      ))
    }
    renderInput={params => (
      <TextField {...params} label={label} size="small"
        placeholder={value.length === 0 ? placeholder : ""} />
    )}
  />
);

// ── Single freeSolo field (content_type, format) ──────────────────────────────
// Uses inputValue (not value) so MUI treats the text as typed input, not a
// selected option — prevents the field from reverting on blur.
const FreeSoloField = ({ label, value, onChange, options }) => (
  <Autocomplete
    freeSolo
    options={options}
    inputValue={value || ""}
    onInputChange={(_, v, reason) => {
      if (reason === "input") onChange(v.toUpperCase().replace(/\s+/g, "_") || "");
    }}
    onChange={(_, v) => {
      // User clicked an option from the dropdown
      if (typeof v === "string" && v) onChange(v.toUpperCase().replace(/\s+/g, "_"));
    }}
    renderInput={p => <TextField {...p} label={label} size="small" />}
  />
);

// ── Main component ────────────────────────────────────────────────────────────
const AddFeed = () => {
  const { admin, token } = isAutheticated();
  const history = useHistory();
  const { feedId } = useParams();
  const isEdit = Boolean(feedId);

  const [form,       setForm]       = useState(INITIAL);
  const [groups,     setGroups]     = useState([]);
  const [companies,  setCompanies]  = useState([]);
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState(null);
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState("");
  const [loading,    setLoading]    = useState(isEdit);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    getFeedGroups(admin._id, token).then(d => setGroups(Array.isArray(d) ? d : []));
    getAllCompanies(admin._id, token).then(d => setCompanies(Array.isArray(d) ? d : []));
  }, [admin._id, token]);

  useEffect(() => {
    if (!isEdit) return;
    getFeed(admin._id, token, feedId).then(d => {
      if (d?.error) { setError(d.error); setLoading(false); return; }
      setForm({
        url:                  d.url || "",
        title:                d.title || "",
        group_id:             d.group_id?._id || d.group_id || "",
        is_active:            d.is_active ?? true,
        strip_title_source:   d.strip_title_source ?? true,
        source_name:          d.source_name || "",
        source_code:          d.source_code || "",
        content_type:         d.content_type || "CRAWLER",
        format:               d.format || "CRAWLER",
        poll_interval_mode:   d.poll_interval_mode || "global",
        poll_interval_seconds:d.poll_interval_seconds || 900,
        poll_random:          d.poll_random ?? false,
        asset_classes:        d.asset_classes || [],
        sectors:              d.sectors || [],
        topic_codes:          d.topic_codes || [],
        tags:                 d.tags || [],
        regions:              d.regions || [],
        continents:           d.continents || [],
        symbols:              d.symbols || [],
        symbol_rules:         (d.symbol_rules || []).map(r => ({
          keyword:  r.keyword || "",
          symbols:  r.symbols || [],
          tags:     r.tags    || [],
          exchange: r.exchange || "",
        })),
        breaking_keywords:    d.breaking_keywords || ["BREAKING","ALERT","URGENT","FLASH"],
        ignore_keywords:      d.ignore_keywords || [],
        ignore_domains:       d.ignore_domains || [],
        region_rules:         d.region_rules || [],
      });
      setLoading(false);
    });
  }, [admin._id, token, feedId, isEdit]);

  // ── URL validation ──────────────────────────────────────────────────────────
  const handleValidate = async () => {
    if (!form.url.trim()) return;
    setValidating(true); setValidation(null);
    const d = await validateFeedUrl(admin._id, token, form.url.trim());
    setValidating(false);
    if (d?.error) { setValidation({ ok:false, message:d.error }); return; }
    setValidation({ ok:true, message:`Valid feed — ${d.item_count} items` });
    if (d.channel_title && !form.title)       set("title",       d.channel_title);
    if (d.suggested_source_name && !form.source_name) set("source_name", d.suggested_source_name);
    if (d.suggested_source_code && !form.source_code) set("source_code", d.suggested_source_code);
  };

  // ── Continent ↔ Region auto-mapping ────────────────────────────────────────
  const handleContinentChange = (newContinents) => {
    const added = newContinents.filter(c => !form.continents.includes(c));
    let regions = [...form.regions];
    for (const c of added) {
      const isos = c === "GLOBAL"
        ? Object.values(CONTINENT_ISO_MAP).flat()
        : (CONTINENT_ISO_MAP[c] || []);
      isos.forEach(r => { if (!regions.includes(r)) regions.push(r); });
    }
    setForm(p => ({ ...p, continents: newContinents, regions }));
  };

  const handleRegionChange = (newRegions) => {
    const added = newRegions.filter(r => !form.regions.includes(r));
    let continents = [...form.continents];
    for (const r of added) {
      const c = REGION_CONTINENT_MAP[r];
      if (c && !continents.includes(c)) continents.push(c);
    }
    setForm(p => ({ ...p, regions: newRegions, continents }));
  };

  // ── Symbol rules helpers — functional updater to avoid stale closure ────────
  const addSymbolRule    = () => setForm(p => ({ ...p, symbol_rules: [...p.symbol_rules, { keyword:"", symbols:[], tags:[], exchange:"" }] }));
  const removeSymbolRule = (i) => setForm(p => ({ ...p, symbol_rules: p.symbol_rules.filter((_,idx)=>idx!==i) }));
  const updateSymbolRule = (i, k, v) => setForm(p => ({ ...p, symbol_rules: p.symbol_rules.map((r,idx)=>idx===i?{...r,[k]:v}:r) }));

  // ── Region rules helpers — functional updater ────────────────────────────────
  const addRegionRule    = () => setForm(p => ({ ...p, region_rules: [...p.region_rules, { domain:"", regions:[], continents:[] }] }));
  const removeRegionRule = (i) => setForm(p => ({ ...p, region_rules: p.region_rules.filter((_,idx)=>idx!==i) }));
  // single-field update (used for domain text field)
  const updateRegionRule = (i, k, v) => setForm(p => ({ ...p, region_rules: p.region_rules.map((r,idx)=>idx===i?{...r,[k]:v}:r) }));
  // atomic update for region+continent together (avoids two-call stale overwrite)
  const updateRegionRuleRC = (i, regions, continents) => setForm(p => ({
    ...p,
    region_rules: p.region_rules.map((r,idx) => idx===i ? { ...r, regions, continents } : r),
  }));

  // ── Submit ──────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!form.url.trim())   return setError("URL is required");
    if (!form.title.trim()) return setError("Title is required");
    if (!form.group_id)     return setError("Group is required");
    setSaving(true); setError("");
    const payload = { ...form };
    payload.symbol_rules = form.symbol_rules.filter(r =>
      r.keyword.trim() && (r.symbols.length > 0 || r.tags.length > 0)
    );
    payload.region_rules = form.region_rules.filter(r => r.domain.trim());
    const d = isEdit
      ? await updateFeed(admin._id, token, feedId, payload)
      : await createFeed(admin._id, token, payload);
    setSaving(false);
    if (d?.error) return setError(d.error);
    history.push("/sources");
  };

  const companyOptions = companies.map(c => ({ label: `${c.nse} — ${c.nameOfCompany}`, value: c.nse }));

  if (loading) return <Base><Box sx={{p:4,textAlign:"center"}}><CircularProgress/></Box></Base>;

  return (
    <Base>
      <Box sx={{ mb:3, display:"flex", alignItems:"center", gap:1 }}>
        <IconButton size="small" onClick={()=>history.push("/sources")}><ArrowBackIcon/></IconButton>
        <Box>
          <Typography variant="h5">{isEdit ? "Edit Feed" : "Add Feed"}</Typography>
          <Typography variant="body2" color="text.secondary">
            {isEdit ? "Update feed configuration" : "Add a new RSS/Atom feed source"}
          </Typography>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{mb:2}} onClose={()=>setError("")}>{error}</Alert>}

      {/* ── Section 1: Basic ──────────────────────────────────────────────────── */}
      <Paper elevation={0} sx={{ p:3, mb:2, border:"1px solid", borderColor:"divider", borderRadius:2 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{mb:2}}>Basic Info</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box sx={{display:"flex", gap:1}}>
              <TextField label="Feed URL *" size="small" fullWidth value={form.url}
                onChange={e=>{ set("url",e.target.value); setValidation(null); }}
                placeholder="https://feeds.example.com/rss.xml"
                InputProps={{ startAdornment:<LinkIcon sx={{mr:1,color:"text.secondary",fontSize:18}}/> }}/>
              <Button variant="outlined" size="small" sx={{flexShrink:0,minWidth:90}}
                onClick={handleValidate} disabled={validating||!form.url.trim()}>
                {validating ? <CircularProgress size={16}/> : "Validate"}
              </Button>
            </Box>
            {validation && (
              <Alert severity={validation.ok?"success":"error"} icon={validation.ok?<CheckCircleIcon/>:undefined} sx={{mt:1,py:0.5}}>
                {validation.message}
              </Alert>
            )}
          </Grid>
          <Grid item xs={12} sm={8}>
            <TextField label="Title *" size="small" fullWidth value={form.title} onChange={e=>set("title",e.target.value)}/>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl size="small" fullWidth>
              <InputLabel>Group *</InputLabel>
              <Select value={form.group_id} label="Group *" onChange={e=>set("group_id",e.target.value)}>
                {groups.map(g=><MenuItem key={g._id} value={g._id}>{g.name}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel control={<Switch checked={form.is_active} onChange={e=>set("is_active",e.target.checked)}/>} label="Active"/>
          </Grid>
        </Grid>
      </Paper>

      {/* ── Section 2: Source & Title ─────────────────────────────────────────── */}
      <Accordion elevation={0} sx={{mb:1,border:"1px solid",borderColor:"divider",borderRadius:"8px !important","&:before":{display:"none"}}}>
        <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
          <Typography variant="subtitle2" fontWeight={700}>Source & Title Cleaning</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={form.strip_title_source} onChange={e=>set("strip_title_source",e.target.checked)}/>}
                label={
                  <Box>
                    <Typography variant="body2">Strip source from headline</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Removes " - Source Name" suffix. Aggregator feeds (Google News, Yahoo Finance) are
                      automatically skipped so the real publisher is extracted, not the aggregator.
                    </Typography>
                  </Box>
                }/>
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField label="Source name" size="small" fullWidth value={form.source_name}
                onChange={e=>set("source_name",e.target.value)} placeholder="e.g. Business Standard"
                helperText="Auto-filled from feed URL validation — overrides per-article extraction"/>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField label="Source code" size="small" fullWidth value={form.source_code}
                onChange={e=>set("source_code",e.target.value.toUpperCase())} placeholder="BUSINESS_STANDARD"
                helperText="Auto-generated if blank"/>
            </Grid>
            <Grid item xs={12} sm={2}>
              <FreeSoloField label="Content type" value={form.content_type}
                onChange={v=>set("content_type",v)} options={CT_OPTS}/>
              <Typography variant="caption" color="text.secondary">Type or choose — default CRAWLER</Typography>
            </Grid>
            <Grid item xs={12} sm={2}>
              <FreeSoloField label="Format" value={form.format}
                onChange={v=>set("format",v)} options={FMT_OPTS}/>
              <Typography variant="caption" color="text.secondary">Type or choose — default CRAWLER</Typography>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* ── Section 3: Poll Interval ──────────────────────────────────────────── */}
      <Accordion elevation={0} sx={{mb:1,border:"1px solid",borderColor:"divider",borderRadius:"8px !important","&:before":{display:"none"}}}>
        <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
          <Typography variant="subtitle2" fontWeight={700}>Poll Interval</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{mb:2}}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{mb:1}}>
              Hierarchy: Individual &gt; Group &gt; Global (default 15 min)
            </Typography>
            <ToggleButtonGroup exclusive size="small" value={form.poll_interval_mode}
              onChange={(_,v)=>v&&set("poll_interval_mode",v)}>
              <ToggleButton value="global">Global default</ToggleButton>
              <ToggleButton value="group">Group setting</ToggleButton>
              <ToggleButton value="individual">Individual</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          {form.poll_interval_mode === "individual" && (
            <TextField label="Interval (seconds)" size="small" type="number" sx={{width:200}}
              value={form.poll_interval_seconds}
              onChange={e=>set("poll_interval_seconds",Math.max(60,Number(e.target.value)))}
              inputProps={{min:60}} helperText="Minimum 60 seconds"/>
          )}
          <Box sx={{mt:2}}>
            <FormControlLabel
              control={<Switch checked={form.poll_random} onChange={e=>set("poll_random",e.target.checked)}/>}
              label={
                <Box>
                  <Typography variant="body2">Randomize poll within interval</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Poll at a random time within the interval window instead of at a fixed cadence.
                    Useful to spread load when many feeds share the same interval.
                  </Typography>
                </Box>
              }/>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* ── Section 4: Classification ─────────────────────────────────────────── */}
      <Accordion elevation={0} sx={{mb:1,border:"1px solid",borderColor:"divider",borderRadius:"8px !important","&:before":{display:"none"}}}>
        <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
          <Typography variant="subtitle2" fontWeight={700}>Classification</Typography>
          {(form.asset_classes.length+form.sectors.length+form.regions.length) > 0 &&
            <Chip size="small" label={form.asset_classes.length+form.sectors.length+form.regions.length} sx={{ml:1}}/>}
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <ChipInput label="Asset classes" value={form.asset_classes} onChange={v=>set("asset_classes",v)} options={ASSET_OPTS}/>
            </Grid>
            <Grid item xs={12} sm={6}>
              <ChipInput label="Sectors" value={form.sectors} onChange={v=>set("sectors",v)} options={SECTOR_OPTS}/>
            </Grid>
            <Grid item xs={12} sm={6}>
              <ChipInput label="Topic codes" value={form.topic_codes} onChange={v=>set("topic_codes",v)} options={TOPIC_OPTS}/>
            </Grid>
            <Grid item xs={12} sm={6}>
              <ChipInput label="Tags (custom)" value={form.tags} onChange={v=>set("tags",v)} placeholder="LARGE_CAP, PSU…"/>
            </Grid>
            <Grid item xs={12} sm={6}>
              <ChipInput label="Continents" value={form.continents} onChange={handleContinentChange} options={CONT_OPTS}
                placeholder="ASIA_PACIFIC, EUROPE…"/>
              <Typography variant="caption" color="text.secondary">
                Selecting a continent auto-fills its ISO region codes below
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <ChipInput label="Regions (ISO 3166-1 alpha-2)" value={form.regions} onChange={handleRegionChange} options={REGION_OPTS}
                placeholder="IN, US, GB…"/>
              <Typography variant="caption" color="text.secondary">
                Selecting a region auto-adds its continent. GLOBAL fills all regions.
              </Typography>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* ── Section 5: Symbols ───────────────────────────────────────────────── */}
      <Accordion elevation={0} sx={{mb:1,border:"1px solid",borderColor:"divider",borderRadius:"8px !important","&:before":{display:"none"}}}>
        <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
          <Typography variant="subtitle2" fontWeight={700}>Symbol & Tag Tagging</Typography>
          {(form.symbols.length+form.symbol_rules.length) > 0 &&
            <Chip size="small" label={`${form.symbols.length} fixed · ${form.symbol_rules.length} rules`} sx={{ml:1}}/>}
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="caption" color="text.secondary" display="block" sx={{mb:2}}>
            <b>Fixed symbols</b> — tagged on every article from this feed, no text scan.<br/>
            <b>Keyword rules</b> — scan title + body; when keyword found, tag the selected symbols AND custom tags (additive with fixed).
            Custom tags can be anything: sectors, themes, labels (e.g. "ENGINEERING", "PSU").
          </Typography>

          {/* Fixed symbols */}
          <Box sx={{mb:3}}>
            <Typography variant="body2" fontWeight={600} sx={{mb:1}}>Fixed symbols</Typography>
            <Autocomplete multiple size="small"
              options={companyOptions}
              value={companyOptions.filter(o=>form.symbols.includes(o.value))}
              onChange={(_,v)=>set("symbols",v.map(x=>x.value))}
              getOptionLabel={o=>o.label}
              isOptionEqualToValue={(o,v)=>o.value===v.value}
              renderInput={p=><TextField {...p} label="Select from companies"/>}
              renderTags={(v,getTagProps)=>v.map((o,i)=>(
                <Chip {...getTagProps({index:i})} label={o.value} size="small"/>
              ))}/>
          </Box>

          {/* Keyword rules */}
          <Box sx={{display:"flex",justifyContent:"space-between",alignItems:"center",mb:1}}>
            <Typography variant="body2" fontWeight={600}>Keyword → Symbols / Tags rules</Typography>
            <Button size="small" startIcon={<AddIcon/>} onClick={addSymbolRule}>Add rule</Button>
          </Box>

          {form.symbol_rules.length > 0 && (
            <Table size="small" sx={{"& .MuiTableCell-root":{verticalAlign:"top",pt:1.5}}}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{fontWeight:600,width:"22%"}}>Keyword in article</TableCell>
                  <TableCell sx={{fontWeight:600,width:"35%"}}>Symbols (from companies)</TableCell>
                  <TableCell sx={{fontWeight:600,width:"35%"}}>Custom tags</TableCell>
                  <TableCell sx={{width:"8%"}}/>
                </TableRow>
              </TableHead>
              <TableBody>
                {form.symbol_rules.map((r,i)=>(
                  <TableRow key={i}>
                    <TableCell>
                      <TextField size="small" fullWidth value={r.keyword}
                        placeholder='e.g. "BHEL"'
                        onChange={e=>updateSymbolRule(i,"keyword",e.target.value)}/>
                    </TableCell>
                    <TableCell>
                      <Autocomplete multiple size="small"
                        options={companyOptions.filter(o=>!r.symbols.includes(o.value))}
                        value={companyOptions.filter(o=>r.symbols.includes(o.value))}
                        onChange={(_,v)=>updateSymbolRule(i,"symbols",v.map(x=>x.value))}
                        getOptionLabel={o=>o.label}
                        isOptionEqualToValue={(o,v)=>o.value===v.value}
                        renderInput={p=><TextField {...p} placeholder="Search symbol…" size="small"/>}
                        renderTags={(v,gtp)=>v.map((o,idx)=>(
                          <Chip {...gtp({index:idx})} label={o.value} size="small"/>
                        ))}/>
                    </TableCell>
                    <TableCell>
                      <ChipInput label="" value={r.tags}
                        onChange={v=>updateSymbolRule(i,"tags",v)}
                        placeholder="ENGINEERING, PSU…"/>
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" color="error" onClick={()=>removeSymbolRule(i)}>
                        <DeleteIcon fontSize="small"/>
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </AccordionDetails>
      </Accordion>

      {/* ── Section 6: Breaking & Filters ────────────────────────────────────── */}
      <Accordion elevation={0} sx={{mb:1,border:"1px solid",borderColor:"divider",borderRadius:"8px !important","&:before":{display:"none"}}}>
        <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
          <Typography variant="subtitle2" fontWeight={700}>Breaking Keywords & Filters</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <ChipInput label="Breaking keywords" value={form.breaking_keywords}
                onChange={v=>set("breaking_keywords",v)} placeholder="BREAKING, ALERT…"/>
              <Typography variant="caption" color="text.secondary">Headline containing any of these → is_breaking = true</Typography>
            </Grid>
            <Grid item xs={12}><Divider/></Grid>
            <Grid item xs={12} sm={6}>
              <ChipInput label="Ignore keywords (this feed)" value={form.ignore_keywords}
                onChange={v=>set("ignore_keywords",v)} placeholder="sponsored, advertisement…" lowercase/>
              <Typography variant="caption" color="text.secondary">Merged with global + group ignore rules</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <ChipInput label="Ignore domains (this feed)" value={form.ignore_domains}
                onChange={v=>set("ignore_domains",v)} placeholder="msn.com, rediff.com…" lowercase/>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* ── Section 7: Region Rules ───────────────────────────────────────────── */}
      <Accordion elevation={0} sx={{mb:3,border:"1px solid",borderColor:"divider",borderRadius:"8px !important","&:before":{display:"none"}}}>
        <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
          <Typography variant="subtitle2" fontWeight={700}>Region Rules (advanced)</Typography>
          {form.region_rules.length > 0 && <Chip size="small" label={form.region_rules.length} sx={{ml:1}}/>}
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="caption" color="text.secondary" display="block" sx={{mb:1.5}}>
            Override region/continent tags based on the article's source domain. Supports continent auto-fill.
          </Typography>
          <Box sx={{display:"flex",justifyContent:"flex-end",mb:1}}>
            <Button size="small" startIcon={<AddIcon/>} onClick={addRegionRule}>Add rule</Button>
          </Box>
          {form.region_rules.length > 0 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{fontWeight:600}}>Domain</TableCell>
                  <TableCell sx={{fontWeight:600}}>Regions (ISO)</TableCell>
                  <TableCell sx={{fontWeight:600}}>Continents</TableCell>
                  <TableCell/>
                </TableRow>
              </TableHead>
              <TableBody>
                {form.region_rules.map((r,i)=>(
                  <TableRow key={i}>
                    <TableCell sx={{verticalAlign:"top",pt:1.5}}>
                      <TextField size="small" fullWidth value={r.domain} placeholder="moneycontrol.com"
                        onChange={e=>updateRegionRule(i,"domain",e.target.value.toLowerCase())}/>
                    </TableCell>
                    <TableCell sx={{verticalAlign:"top"}}>
                      <ChipInput label="" value={r.regions}
                        onChange={newR=>{
                          const added = newR.filter(x=>!r.regions.includes(x));
                          let conts = [...r.continents];
                          added.forEach(x=>{ const c=REGION_CONTINENT_MAP[x]; if(c&&!conts.includes(c)) conts.push(c); });
                          updateRegionRuleRC(i, newR, conts);
                        }}
                        options={REGION_OPTS}/>
                    </TableCell>
                    <TableCell sx={{verticalAlign:"top"}}>
                      <ChipInput label="" value={r.continents}
                        onChange={newC=>{
                          const added = newC.filter(c=>!r.continents.includes(c));
                          let regions = [...r.regions];
                          added.forEach(c=>{
                            const isos = c==="GLOBAL" ? Object.values(CONTINENT_ISO_MAP).flat() : (CONTINENT_ISO_MAP[c]||[]);
                            isos.forEach(x=>{ if(!regions.includes(x)) regions.push(x); });
                          });
                          updateRegionRuleRC(i, regions, newC);
                        }}
                        options={CONT_OPTS}/>
                    </TableCell>
                    <TableCell sx={{verticalAlign:"top",pt:1.5}}>
                      <IconButton size="small" color="error" onClick={()=>removeRegionRule(i)}>
                        <DeleteIcon fontSize="small"/>
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </AccordionDetails>
      </Accordion>

      {/* ── Save ─────────────────────────────────────────────────────────────── */}
      <Stack direction="row" spacing={2}>
        <Button variant="contained" size="large" onClick={handleSubmit} disabled={saving}>
          {saving ? <CircularProgress size={20}/> : (isEdit ? "Save Changes" : "Add Feed")}
        </Button>
        <Button variant="outlined" size="large" onClick={()=>history.push("/sources")}>Cancel</Button>
      </Stack>
    </Base>
  );
};

export default AddFeed;
